import React, { useContext, useMemo } from "react";
import MUIDataTable from "mui-datatables";
import { useQuery } from "react-apollo-hooks";
import { observer } from "mobx-react-lite";
import { textLabels } from "../dataTablesLocalization";
import { StoreContext } from "../../store";
import { Grid, Button } from "@material-ui/core";
import gql from "graphql-tag";

const query = gql`
  query getPayments($turma: Int) {
    register(
      order_by: {
        enrollmentByEnrollment: { person: { first_name: asc, last_name: asc } }
      }
      where: { turma: { _eq: $turma }, deleted_at: { _is_null: true } }
    ) {
      id
      is_professor
      is_tutor
      is_team_member
      is_monitor
      is_audit
      is_evaluator
      suspended
      cancelled
      locked
      dependency
      enrollmentByEnrollment {
        person {
          id
          first_name
          last_name
        }
        payment_plans {
          id
          dia_vencimento
          parcelas
          valor_total
          payments(
            where: {
              _or: [
                { status: { _eq: "paid" } }
                { status: { _eq: "pending" } }
                { status: { _eq: "expired" }, disabled: { _eq: false } }
              ]
              deleted_at: { _is_null: true }
            }
            order_by: { due_date: asc }
          ) {
            id
            payment_id
            due_date
            status
            disabled
            value
          }
        }
      }
    }
  }
`;

const Payments = observer(({ props }) => {
  const store = useContext(StoreContext);
  const turma = store.ui.turma.id;

  const { data, error, loading } = useQuery(query, {
    variables: { turma },
    fetchPolicy: "network-only",
  });

  const { processedData, columnData, monthColumns } = useMemo(() => {
    if (!data || !data.register)
      return { processedData: [], columnData: [], monthColumns: [] };

    const titles = {};
    const planColor = {};
    let dataInicio = "9999-99";
    let dataFim = "0000-00";

    const processedData = data.register.map((register) => {
      const nome = `${register.enrollmentByEnrollment.person.first_name} ${register.enrollmentByEnrollment.person.last_name}`;
      const rowData = {
        id: register.id,
        nome,
        originalRegister: register,
      };

      let title = [];
      if (register.suspended) title.push("SUSPENSO");
      if (register.cancelled) title.push("CANCELADO");
      if (register.locked) title.push("Matrícula trancada");
      if (register.dependency) title.push("fazendo dependência");
      if (register.is_tutor) title.push("Tutor");
      if (register.is_monitor) title.push("Monitor");
      if (register.is_professor) title.push("Professor");
      if (register.is_team_member) title.push("Equipe");
      if (register.is_audit) title.push("Ouvinte");
      if (register.is_evaluator) title.push("Avaliador");
      titles[nome] = title.length > 0 ? ` (${title.join(", ")})` : "";

      const plan = register.enrollmentByEnrollment.payment_plans?.[0];
      if (plan) {
        const valorTotalParcelas = plan.payments.reduce(
          (sum, item) => sum + item.value,
          0
        );
        rowData.dia_vencimento = plan.dia_vencimento;
        rowData.pago = `${
          plan.payments.filter((i) => i.status === "paid").length
        }/${plan.parcelas}`;
        planColor[nome] =
          valorTotalParcelas !== plan.valor_total ? "red" : "black";

        plan.payments.forEach((invoice) => {
          if (
            invoice.status === "paid" ||
            invoice.status === "pending" ||
            (invoice.status === "expired" && !invoice.disabled)
          ) {
            const month = invoice.due_date.substring(0, 7);

            if (month > dataFim) dataFim = month;
            if (month < dataInicio) dataInicio = month;

            rowData[month] = rowData[month] ? `${rowData[month]} ` : "";

            if (invoice.status === "paid") {
              rowData[month] += "✓";
            } else if (invoice.status === "expired") {
              if (!invoice.disabled) {
                rowData[month] += "ATRASO";
              }
            } else {
              const today = new Date().toISOString().split("T")[0];
              rowData[month] += invoice.due_date < today ? "ATRASO" : "aberto";
            }
          }
        });
      }

      return rowData;
    });

    const columnData = [
      {
        name: "nome",
        options: {
          setCellProps: () => ({
            style: {
              minWidth: "300px",
              whiteSpace: "nowrap",
              position: "sticky",
              left: "0",
              background: "white",
            },
          }),
          setCellHeaderProps: () => ({
            style: {
              whiteSpace: "nowrap",
              position: "sticky",
              left: 0,
              background: "white",
            },
          }),
          customBodyRender: (value) => `${value}${titles[value] || ""}`,
        },
      },
      {
        name: "dia_vencimento",
      },
      {
        name: "pago",
        options: {
          customBodyRender: (value, tableMeta) => {
            const color = planColor[tableMeta.rowData[0]] || "black";
            return <b style={{ color }}>{value}</b>;
          },
        },
      },
    ];

    const monthColumns = [];
    if (dataInicio !== "9999-99" && dataFim !== "0000-00") {
      let [ano, mes] = dataInicio.split("-").map((n) => parseInt(n, 10));
      const dataFimArr = dataFim.split("-").map((n) => parseInt(n, 10));

      while (
        ano < dataFimArr[0] ||
        (ano === dataFimArr[0] && mes <= dataFimArr[1])
      ) {
        const monthStr = `${ano}-${mes.toString().padStart(2, "0")}`;
        monthColumns.push(monthStr);

        mes++;
        if (mes > 12) {
          ano++;
          mes = 1;
        }
      }
    }

    return {
      processedData,
      columnData,
      monthColumns,
    };
  }, [data]);

  const columns = useMemo(() => {
    return [...columnData, ...monthColumns];
  }, [columnData, monthColumns]);

  if (loading) return <p>Carregando... </p>;
  if (error) return <p>ERROR (Payments.jsx): {error.message}</p>;

  const handleRowClick = (rowData, rowMeta) => {
    const register = processedData[rowMeta.dataIndex];
    props.history.push(
      `/newInvoice?id=${register.originalRegister.enrollmentByEnrollment.person.id}`
    );
  };

  return (
    <>
      <Grid container>
        <div style={{ display: "table", tableLayout: "fixed", width: "100%" }}>
          * Coluna pago em vermelho: a soma total das faturas está diferente do
          valor do plano de pagamento
          <MUIDataTable
            title={`Pagamentos - turma ${store.ui.turma.name}`}
            data={processedData}
            columns={columns}
            options={{
              responsive: "scroll",
              rowsPerPageOptions: [10, 25, 50, 100],
              rowsPerPage: 25,
              selectableRows: false,
              filterType: "multiselect",
              textLabels,
              downloadOptions: {
                filename: `pagamentos-turma-${store.ui.turma.name}.csv`,
                separator: ",",
              },
              onRowClick: handleRowClick,
              print: false,
              viewColumns: true,
              pagination: true,
              enableNestedDataAccess: ".",
            }}
          />
        </div>
      </Grid>
      <Button
        variant="contained"
        color="primary"
        style={{ margin: "30px" }}
        onClick={() => {
          props.history.push("/report");
        }}
      >
        Relatório mensal
      </Button>
    </>
  );
});

export default Payments;
